import type { Tenant } from '../../../../../../configuration/types';
import type {
    Driver,
    DriverStatus,
    NewIdentification,
    SidebarContentId,
    Tag,
    TagToCreate,
    User,
} from '../../../../../../types';
import { DriverSidebar } from '../components/DriverSidebar';
import {
    changeTab,
    confirmDiscardAndChangeTab,
    deleteDriverSuccess,
    getContentIdRtk,
    getFormErrorsRtk,
    getNewIdentificationValueRtk,
    getNextTabRtk,
    getPendingIdentificationDeletionsRtk,
    getSelectedDriverRtk,
    getSelectedDriverWithoutEditsRtk,
    isChangedRtk,
    keepEditing,
    saveDriverFailed,
    saveDriverInProgress,
    saveDriverSuccess,
} from '../../sidebarSlice';
import { getDriverStatusesRtk, getUsersRtk, isInviteInProgressRtk } from '../../../../appSlice';
import { cancelSidebarThunk, deselectDriver } from '../../../../../crossSliceThunks';
import { getTenant } from '../../../../../../configuration/login/loginSlice';
import { setUsersToDelete, toggleDeleteUserDialog } from '../../tabs/user/userSlice';
import { clearAllTags } from '../../tabs/groups/groupSlice';
import { useAppDispatch, useAppSelector } from '../../../../../../configuration/setup/hooks';
import {
    useAddIdentificationMutation,
    useDeleteDriverMutation,
    useDeleteIdentificationMutation,
    useLazyGetDriverQuery,
    useSaveDriverMutation,
    useTagDriverMutation,
    useUntagDriverMutation,
} from '../../../../services/driversApi';
import { useCreateTagMutation } from '../../../../services/tagsApi';
import {
    showDeleteDriverSuccessNotification,
    showErrorNotification,
    showSuccessNotification,
} from '../../../../notifications/notifications';
import { ErrorOrigin } from '../../../../services/apiUtils';
import { getApiErrorCode, isApiErrorWithCode } from '../../../../services/apiErrors';

export interface SidebarPropertiesFromState {
    selectedDriver: Driver | null;
    selectedDriverWithoutEdits: Driver | null;
    driverStatuses: DriverStatus[];
    users: User[];
    tagsToCreate: TagToCreate[];
    tagsToAdd: Tag[];
    tagsToRemove: Tag[];
    contentId: string;
    formErrors: { [p: string]: string | undefined };
    fetchDriverInProgress: boolean;
    inviteUserRequested: boolean;
    hasChanges: boolean;
    showUnsavedChangesDialog: boolean;
    newIdentification: NewIdentification | null;
    identificationIdsPendingForDeletion: Set<string>;
    nextTab: SidebarContentId | null;
    tenant: Tenant | null;
}

export interface SidebarPropertiesFromDispatch {
    handleCancel: () => void;
    handleKeepEditing: () => void;
    handleTabChange: (contentId: SidebarContentId) => void;
    handleDiscardClose: () => void;
    deleteDriver: (driver: Driver) => void;
    saveSelectedDriver: (
        driver: Driver,
        tagsToCreate: TagToCreate[],
        tagsToAdd: Tag[],
        tagsToRemove: Tag[],
        newIdentification: NewIdentification | null,
        identificationIdsPendingForDeletion: Set<string>
    ) => void;
    confirmDiscardAndChangeTab: () => void;
    toggleDeleteUserDialog: (show: boolean) => void;
    setUsersToDelete: (users: User[]) => void;
}

const buildIdentification = (newIdentification: NewIdentification): Driver['identifications'][number] | null => {
    if (!newIdentification.value || !newIdentification.type) {
        return null;
    }

    return {
        id: crypto.randomUUID(),
        value: newIdentification.value,
        type: newIdentification.type,
    };
};

export const SidebarContainer = () => {
    const dispatch = useAppDispatch();

    // const selectedDriverId = useAppSelector(state => state.app.selectedDriverId);
    const selectedDriver = useAppSelector(getSelectedDriverRtk);
    const selectedDriverWithoutEdits = useAppSelector(getSelectedDriverWithoutEditsRtk);
    const driverStatuses = useAppSelector(getDriverStatusesRtk);
    const users = useAppSelector(getUsersRtk);
    const tagsToCreate = useAppSelector(state => state.groups.tagsToCreate);
    const tagsToAdd = useAppSelector(state => state.groups.tagsToAdd);
    const tagsToRemove = useAppSelector(state => state.groups.tagsToRemove);
    const contentId = useAppSelector(getContentIdRtk);
    const formErrors = useAppSelector(getFormErrorsRtk);
    const hasChanges = useAppSelector(isChangedRtk);
    const inviteUserRequested = useAppSelector(isInviteInProgressRtk);
    const showUnsavedChangesDialog = useAppSelector(state => state.sidebar.showUnsavedChangesDialog);
    const newIdentification = useAppSelector(getNewIdentificationValueRtk);
    const identificationIdsPendingForDeletion = new Set(useAppSelector(getPendingIdentificationDeletionsRtk));
    const nextTab = useAppSelector(getNextTabRtk);
    const tenant = useAppSelector(getTenant);

    const [loadDriver] = useLazyGetDriverQuery();
    const [saveDriverMutation] = useSaveDriverMutation();
    const [deleteDriverMutation] = useDeleteDriverMutation();
    const [createTagMutation] = useCreateTagMutation();
    const [tagDriverMutation] = useTagDriverMutation();
    const [untagDriverMutation] = useUntagDriverMutation();
    const [addIdentificationMutation] = useAddIdentificationMutation();
    const [deleteIdentificationMutation] = useDeleteIdentificationMutation();

    const handleDriverAdminError = (error: object) => {
        if (isApiErrorWithCode(error)) {
            showErrorNotification(getApiErrorCode(error, ErrorOrigin.DRIVER_ADMINISTRATION));
        }
    };

    // const handleTagError = (error: object) => {
    //     if (isApiErrorWithCode(error)) {
    //         showErrorNotification(getApiErrorCode(error, ErrorOrigin.TAG_SERVICE));
    //     }
    // };

    const saveSelectedDriverHandler: SidebarPropertiesFromDispatch['saveSelectedDriver'] = async (
        driver,
        tagsToCreateArg,
        tagsToAddArg,
        tagsToRemoveArg,
        newIdentificationArg,
        identificationIdsPendingForDeletionArg
    ) => {
        dispatch(saveDriverInProgress());
        let errorOrigin: ErrorOrigin = ErrorOrigin.DRIVER_ADMINISTRATION;

        try {
            for (const tag of tagsToAddArg) {
                await tagDriverMutation({ driverId: driver.driverId, tagId: tag.id }).unwrap();
            }

            for (const tag of tagsToRemoveArg) {
                await untagDriverMutation({ driverId: driver.driverId, tagId: tag.id }).unwrap();
            }

            for (const tag of tagsToCreateArg) {
                errorOrigin = ErrorOrigin.TAG_SERVICE;
                const createdTag = await createTagMutation({
                    tagName: tag.name,
                    accountId: driver.accountId,
                }).unwrap();
                errorOrigin = ErrorOrigin.DRIVER_ADMINISTRATION;

                await tagDriverMutation({ driverId: driver.driverId, tagId: createdTag.id }).unwrap();
            }

            for (const identificationId of Array.from(identificationIdsPendingForDeletionArg)) {
                await deleteIdentificationMutation({
                    driverId: driver.driverId,
                    identificationId,
                }).unwrap();
            }

            if (newIdentificationArg) {
                const identification = buildIdentification(newIdentificationArg);
                if (identification) {
                    await addIdentificationMutation({
                        driverId: driver.driverId,
                        identification,
                    }).unwrap();
                }
            }

            await saveDriverMutation(driver).unwrap();
            const refreshedDriver = await loadDriver(driver.driverId, true).unwrap();

            dispatch(saveDriverSuccess(refreshedDriver));
            dispatch(clearAllTags());
            showSuccessNotification('intl-msg:save.success');
        } catch (error) {
            if (isApiErrorWithCode(error)) {
                showErrorNotification(getApiErrorCode(error, errorOrigin));
            }
            dispatch(saveDriverFailed());
        }
    };

    const deleteDriverHandler: SidebarPropertiesFromDispatch['deleteDriver'] = async driver => {
        try {
            await deleteDriverMutation(driver).unwrap();
            showDeleteDriverSuccessNotification(driver);
            dispatch(deleteDriverSuccess());
            dispatch(deselectDriver());
        } catch (error) {
            handleDriverAdminError(error as object);
        }
    };

    const props: SidebarPropertiesFromState & SidebarPropertiesFromDispatch = {
        selectedDriver,
        selectedDriverWithoutEdits,
        driverStatuses,
        users,
        tagsToCreate,
        tagsToAdd,
        tagsToRemove,
        contentId,
        formErrors,
        hasChanges,
        fetchDriverInProgress: false,
        inviteUserRequested,
        showUnsavedChangesDialog,
        newIdentification,
        identificationIdsPendingForDeletion,
        nextTab,
        tenant,
        handleCancel: () => {
            dispatch(cancelSidebarThunk());
        },
        handleKeepEditing: () => {
            dispatch(keepEditing());
        },
        handleTabChange: nextContentId => {
            dispatch(changeTab(nextContentId));
        },
        handleDiscardClose: () => {
            dispatch(deselectDriver());
        },
        deleteDriver: deleteDriverHandler,
        saveSelectedDriver: saveSelectedDriverHandler,
        confirmDiscardAndChangeTab: () => {
            dispatch(confirmDiscardAndChangeTab());
            dispatch(clearAllTags());
        },
        toggleDeleteUserDialog: show => {
            dispatch(toggleDeleteUserDialog(show));
        },
        setUsersToDelete: nextUsers => {
            dispatch(setUsersToDelete(nextUsers));
        },
    };

    return <DriverSidebar {...props} />;
};
